import { useMemo, useState } from 'react';
import { palette } from '../../styles/tokens';

/**
 * TeamMemberPicker — reusable picker for crew/team members.
 *
 * Caller provides a flat `members` array. Picker handles search + status
 * filter chips + selection state. Promoted from Dispatch/AssignModal's
 * inline scroll-list to a shared primitive (Q5 Phase 5, closes A-040).
 *
 * Status taxonomy: members use the coarse 'available' | 'on_job' | 'off'
 * bucket. Callers with finer-grained source data (e.g. Dispatch's 'on_job'
 * vs 'en_route' vs 'scheduled' vs 'off_today') are responsible for mapping
 * before passing to the picker — that boundary keeps the primitive
 * decoupled from any one consumer's domain shape.
 */

export type TeamMemberStatus = 'available' | 'on_job' | 'off';

export interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  role?: string | null;
  employment_type?: string | null;
  status: TeamMemberStatus;
  busyLabel?: string | null; // optional sub-meta, e.g. "BUSY: <job>"
}

export type TeamMemberPickerStatusFilter = 'all' | TeamMemberStatus;

export interface TeamMemberPickerProps {
  members: TeamMember[];
  value: string | null;
  onChange: (userId: string | null) => void;
  statusFilter?: TeamMemberPickerStatusFilter[];
  defaultStatus?: TeamMemberPickerStatusFilter;
  showSearch?: boolean;
  highlightCurrent?: string;
  disabled?: boolean;
  emptyLabel?: string;
}

const STATUS_LABEL: Record<TeamMemberStatus, string> = {
  available: 'Available',
  on_job: 'On job',
  off: 'Off',
};

const STATUS_DOT: Record<TeamMemberStatus, string> = {
  available: '#107C10',
  on_job: '#D46A0E',
  off: palette.g400,
};

const SEARCH_THRESHOLD = 5;

const WRAPPER: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  width: '100%',
};

const SEARCH: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontSize: 13,
  color: palette.g800,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const CHIPS_ROW: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
};

const CHIP_BASE: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 14,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontSize: 12,
  fontWeight: 500,
  color: palette.g600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const CHIP_ACTIVE: React.CSSProperties = {
  ...CHIP_BASE,
  background: palette.accentWash,
  borderColor: palette.accent,
  color: palette.accentDeep,
  fontWeight: 600,
};

const LIST: React.CSSProperties = {
  maxHeight: 320,
  overflowY: 'auto',
  borderRadius: 8,
  border: `1px solid ${palette.g100}`,
};

const ROW_BASE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  cursor: 'pointer',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  background: palette.white,
  borderBottom: `1px solid ${palette.g100}`,
  fontFamily: 'inherit',
};

const ROW_SELECTED: React.CSSProperties = {
  ...ROW_BASE,
  background: palette.accentWash,
};

const ROW_CURRENT: React.CSSProperties = {
  ...ROW_BASE,
  background: palette.g50,
};

const ROW_DISABLED: React.CSSProperties = {
  ...ROW_BASE,
  opacity: 0.6,
};

const AVATAR: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 16,
  background: palette.accentWash,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 600,
  color: palette.accentDeep,
  flexShrink: 0,
};

const NAME: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: palette.g800,
};

const META: React.CSSProperties = {
  fontSize: 11,
  color: palette.g400,
};

const STATUS_BLOCK: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginLeft: 'auto',
};

const STATUS_DOT_STYLE: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: 3,
};

const EMPTY: React.CSSProperties = {
  padding: 16,
  textAlign: 'center' as const,
  fontSize: 13,
  color: palette.g500,
};

const CURRENT_BADGE: React.CSSProperties = {
  fontSize: 9,
  padding: '1px 6px',
  borderRadius: 3,
  background: palette.g200,
  color: palette.g700,
  fontWeight: 600,
  marginLeft: 4,
};

export function TeamMemberPicker({
  members,
  value,
  onChange,
  statusFilter = ['all', 'available', 'on_job', 'off'],
  defaultStatus = 'all',
  showSearch,
  highlightCurrent,
  disabled = false,
  emptyLabel = 'No team members.',
}: TeamMemberPickerProps) {
  const [query, setQuery] = useState('');
  const [activeStatus, setActiveStatus] = useState<TeamMemberPickerStatusFilter>(defaultStatus);

  const search = showSearch ?? members.length >= SEARCH_THRESHOLD;

  const filtered = useMemo(() => {
    let out = members;
    if (activeStatus !== 'all') out = out.filter((m) => m.status === activeStatus);
    if (query) {
      const q = query.toLowerCase();
      out = out.filter((m) =>
        `${m.first_name} ${m.last_name}`.toLowerCase().includes(q)
        || (m.role ?? '').toLowerCase().includes(q),
      );
    }
    // Sort: available > on_job > off, then alpha by first name.
    return [...out].sort((a, b) => {
      const order = { available: 0, on_job: 1, off: 2 };
      const ord = order[a.status] - order[b.status];
      if (ord !== 0) return ord;
      return a.first_name.localeCompare(b.first_name);
    });
  }, [members, activeStatus, query]);

  return (
    <div style={WRAPPER}>
      {search && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search crew…"
          autoFocus
          disabled={disabled}
          style={SEARCH}
        />
      )}

      {statusFilter.length > 1 && (
        <div style={CHIPS_ROW}>
          {statusFilter.map((s) => {
            const label = s === 'all' ? 'All' : STATUS_LABEL[s];
            const active = activeStatus === s;
            return (
              <button
                key={s}
                type="button"
                disabled={disabled}
                onClick={() => setActiveStatus(s)}
                style={active ? CHIP_ACTIVE : CHIP_BASE}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div style={LIST}>
        {filtered.length === 0 && (
          <div style={EMPTY}>{query ? 'No matches.' : emptyLabel}</div>
        )}
        {filtered.map((m) => {
          const isSelected = value === m.id;
          const isCurrent = highlightCurrent === m.id;
          const rowStyle = disabled
            ? ROW_DISABLED
            : isSelected
            ? ROW_SELECTED
            : isCurrent
            ? ROW_CURRENT
            : ROW_BASE;
          const initials = `${m.first_name.charAt(0)}${m.last_name.charAt(0)}`;
          return (
            <button
              key={m.id}
              type="button"
              disabled={disabled}
              onClick={() => onChange(isSelected ? null : m.id)}
              style={rowStyle}
            >
              <div style={AVATAR}>{initials}</div>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <div style={NAME}>
                  {m.first_name} {m.last_name}
                  {isCurrent && <span style={CURRENT_BADGE}>CURRENT</span>}
                </div>
                {(m.role || m.employment_type) && (
                  <div style={META}>
                    {m.role}
                    {m.role && m.employment_type ? ' · ' : ''}
                    {m.employment_type}
                  </div>
                )}
              </div>
              <div style={STATUS_BLOCK}>
                <span style={{ ...STATUS_DOT_STYLE, background: STATUS_DOT[m.status] }} />
                <span style={{ fontSize: 10, color: STATUS_DOT[m.status], fontWeight: 500 }}>
                  {STATUS_LABEL[m.status]}
                </span>
              </div>
              {m.busyLabel && m.status !== 'available' && (
                <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: palette.amberWash, color: palette.amber, fontWeight: 600, marginLeft: 6 }}>
                  {m.busyLabel}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
